import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SERVICES, type RequestDto, type RequestTriage, REQUEST_TRIAGE } from '@shared';
import { apiGet } from '../../lib/api';
import { StatusChip } from '../../components/StatusChip';
import { TriageDrawer } from '../../components/TriageDrawer';

type Filter = 'all' | RequestTriage;

const TRIAGE_LABELS: Record<RequestTriage, string> = {
  unread: 'Unread',
  reviewing: 'Reviewing',
  proposed: 'Proposed',
  rejected: 'Rejected',
};

export function AdminInbox() {
  const [rows, setRows] = useState<RequestDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ requests: RequestDto[] }>('/api/requests/inbox')
      .then((r) => setRows(r.requests))
      .catch((err: Error) => setError(err.message ?? 'Failed to load inbox.'));
  }, []);

  const selected = useMemo(
    () => (selectedId ? (rows ?? []).find((r) => r.id === selectedId) ?? null : null),
    [rows, selectedId],
  );

  const handlePatched = useCallback((next: RequestDto) => {
    setRows((prev) => (prev ?? []).map((r) => (r.id === next.id ? { ...r, ...next } : r)));
  }, []);

  const list = useMemo(
    () => (rows ?? []).filter((r) => filter === 'all' ? true : r.triage === filter),
    [rows, filter],
  );

  const counts = useMemo(() => {
    const c = { unread: 0, reviewing: 0, proposed: 0, rejected: 0 } as Record<RequestTriage, number>;
    for (const r of rows ?? []) c[r.triage] = (c[r.triage] ?? 0) + 1;
    return c;
  }, [rows]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 className="portal-h1">Inbox</h1>
          <p className="portal-sub">
            Briefs submitted via the portal. Triage to advance to a proposal, or reject if out of scope.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link className="btn ghost" to="/admin/pipeline">Pipeline →</Link>
        </div>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Unread</div>
          <div className="val">{counts.unread}</div>
          <div className="delta">Awaiting first triage</div>
        </div>
        <div className="card">
          <div className="lbl">Reviewing</div>
          <div className="val">{counts.reviewing}</div>
          <div className="delta">In active triage</div>
        </div>
        <div className="card">
          <div className="lbl">Proposed</div>
          <div className="val">{counts.proposed}</div>
          <div className="delta">Awaiting client acceptance</div>
        </div>
        <div className="card">
          <div className="lbl">Rejected</div>
          <div className="val">{counts.rejected}</div>
          <div className="delta">Out of scope or withdrawn</div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        {(['all', ...REQUEST_TRIAGE] as Filter[]).map((t) => (
          <button key={t} className={filter === t ? 'on' : ''} onClick={() => setFilter(t)}>
            {t === 'all' ? 'All' : TRIAGE_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Client</th>
              <th>Engagement</th>
              <th>Service</th>
              <th>Triage</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>}
            {error && <tr><td colSpan={7} style={{ color: 'var(--red)', fontSize: 13 }}>{error}</td></tr>}
            {rows !== null && !error && list.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No briefs in this view.
                </td>
              </tr>
            )}
            {list.map((r) => {
              const svc = SERVICES.find((s) => s.id === r.service);
              return (
                <tr
                  key={r.id}
                  className={`row-clickable ${r.triage === 'unread' ? 'row-unread' : ''} ${selectedId === r.id ? 'row-selected' : ''}`}
                  onClick={() => setSelectedId(r.id)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(r.id);
                    }
                  }}
                >
                  <td><span className="rid">{r.id}</span></td>
                  <td>{r.client_name ?? r.client_id}</td>
                  <td style={{ fontSize: 13.5 }}>{r.headline}</td>
                  <td style={{ color: 'var(--fg-soft)' }}>{svc?.name ?? r.service}</td>
                  <td>
                    <span className={`chip ${r.triage === 'proposed' ? 'accent' : r.triage === 'reviewing' ? 'amber' : r.triage === 'rejected' ? 'red' : ''}`}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                      {TRIAGE_LABELS[r.triage]}
                    </span>
                  </td>
                  <td><StatusChip status={r.status} /></td>
                  <td style={{ color: 'var(--fg-mute)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                    {r.created_at.slice(0, 10)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <TriageDrawer
          request={selected}
          onClose={() => setSelectedId(null)}
          onPatched={handlePatched}
        />
      )}
    </>
  );
}
